"use client";
import {
  ButtonComponent,
  LoaderComponent,
  PaperComponent,
  SecureDataComponent,
  TextFieldComponent,
} from "@/components";
import {
  CKYC_SCHEMA,
  GET_PRODUCT_APPLICATION,
  IMAGES,
  PRODUCT_HANDLER,
  STAGE,
  STAGE_BASED_ROUTING,
  WEB_EVENTS,
} from "@/lib/constants";
import {
  useGetApplicationQuery,
  useGetCustomerQuery,
  useInitiateCkycMutation,
} from "@/lib/redux";
import { useFormik } from "formik";
import React, { useEffect } from "react";
import { useRouter } from "@/i18n/routing";
import useLogoutHook from "@/lib/hooks/logoutHook";
import {
  FormControl,
  FormHelperText,
  Grid,
  Stack,
  Typography,
} from "@mui/material";
import useErrorHandling from "@/lib/hooks/errorhandling";
import { sendEventAnalytics } from "@/lib/hooks/eventAnalytics";
import moengage from "@moengage/web-sdk";
import Image from "next/image";
import { useTranslations } from "next-intl";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";

function CkycScreen({ product }) {
  //HOOKS
  const router = useRouter();
  const t = useTranslations("CkycDetails");
  const { handleLogout } = useLogoutHook();
  const maxDate = dayjs().subtract(18, "year");
  /*************************************RTK QUERY*****************************/
  const {
    data: applicationData,
    error: applicationFetchError,
    isFetching: fetchingApplications,
    isLoading: isLoadingApplications,
  } = useGetApplicationQuery();

  const productApplication = GET_PRODUCT_APPLICATION(
    PRODUCT_HANDLER[product],
    applicationData?.data
  );

  const {
    data: customerData,
    isLoading: isLoadingCsutomer,
    isError: isErrorCustomer,
    refetch: refetchCustomer,
    isSuccess: isSuccessCustomer,
  } = useGetCustomerQuery();

  const [
    initiateCkyc,
    {
      isLoading: verifyingCkyc,
      isSuccess: ckycSuccess,
      error: verifyCkycError,
    },
  ] = useInitiateCkycMutation();
  const isError = applicationFetchError || verifyCkycError || isErrorCustomer;

  /*************************************EFFECT*****************************/

  //dispaly errors
  useErrorHandling(isError);

  useEffect(() => {
    const productApplication = GET_PRODUCT_APPLICATION(
      PRODUCT_HANDLER[product],
      applicationData?.data
    );
    if (productApplication?.stage === STAGE.CKYC) {
      sendEventAnalytics({
        eventName: WEB_EVENTS.CKYC_STARTED,
        ...productApplication,
        ...customerData?.data,
      });
    }
    if (productApplication?.stage) {
      router.push(STAGE_BASED_ROUTING(productApplication?.stage));
    }
  }, [applicationData?.data]);

  /*************************************ACTION FUNCTIONS*****************************/
  const formik = useFormik({
    enableReinitialize: true,
    validationSchema: CKYC_SCHEMA,
    initialValues: {
      firstName: customerData?.data?.firstName || "",
      lastName: customerData?.data?.lastName || "",
      pan: customerData?.data?.pan || "",
      dob: customerData?.data?.dob || "",
    },
    onSubmit: (values) => {
      initiateCkyc({
        appId: productApplication?.appId,
        dob: customerData?.data?.firstName
          ? values?.dob
          : new Date(values?.dob)?.toLocaleDateString("en-GB"),
        pan: values?.pan?.toUpperCase(),
      });
    },
  });

  const backClick = () => {
    router.push(STAGE_BASED_ROUTING("PAN"));
  };

  /*************************************RENDER*****************************/
  return (
    <Stack>
      <Stack
        direction={"row"}
        alignItems={"center"}
        gap={2}
        onClick={backClick}
        sx={{
          cursor: "pointer",
          px: 2,
        }}
        width={"fit-content"}
      >
        <Image src={IMAGES.backArrow} height={20} width={20} alt="back arrow" />
        <Typography variant="subtitle2">Modify PAN</Typography>
      </Stack>
      <PaperComponent>
        <LoaderComponent
          open={
            fetchingApplications ||
            isLoadingCsutomer ||
            isLoadingApplications ||
            verifyingCkyc
          }
        />
        <Stack>
          <Typography variant="h5">{t("verifypan")}</Typography>

          <Grid container spacing={2} rowSpacing={2} my={2}>
            {customerData?.data?.firstName && (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextFieldComponent
                  allowCapitalization={true}
                  disabled
                  name="firstname"
                  label={t("firstname")}
                  value={formik.values.firstName}
                />
              </Grid>
            )}
            {customerData?.data?.lastName && (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextFieldComponent
                  allowCapitalization={true}
                  disabled
                  name="lastname"
                  label={t("lastname")}
                  value={formik.values.lastName}
                />
              </Grid>
            )}
            <Grid item xs={12} sm={12} md={6} lg={6}>
              <TextFieldComponent
                disabled={customerData?.data?.pan}
                required
                inputPropsStyle={{ textTransform: "uppercase" }}
                name="pan"
                maxLength={10}
                label={t("pannumber")}
                value={formik.values.pan}
                placeholder={t("pannumber")}
                errorMessage={formik?.errors?.pan}
                error={Boolean(formik.touched.pan && formik?.errors?.pan)}
                onBlur={formik.handleBlur}
                onChange={formik.handleChange}
              />
            </Grid>
            {customerData?.data?.dob ? (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <TextFieldComponent
                  disabled
                  name="dob"
                  label={t("dob")}
                  value={formik.values.dob}
                />
              </Grid>
            ) : (
              <Grid item xs={12} sm={12} md={6} lg={6}>
                <LocalizationProvider dateAdapter={AdapterDayjs}>
                  <FormControl fullWidth required>
                    <DatePicker
                      slotProps={{
                        textField: {
                          required: true,
                          error:
                            Boolean(formik.touched?.dob) &&
                            Boolean(formik.errors?.dob),
                        },
                      }}
                      format="DD/MM/YYYY"
                      disableFuture
                      maxDate={maxDate}
                      label={t("dob")}
                      name="dob"
                      value={formik?.values?.dob ? formik?.values?.dob : null}
                      onChange={(value) => {
                        formik.setFieldValue("dob", value);
                        formik.setFieldTouched("dob", true);
                      }}
                      onClose={() => {
                        formik.setFieldTouched("dob", true);
                      }}
                    />
                    {formik?.touched?.dob && formik?.errors?.dob && (
                      <FormHelperText sx={{ color: "#d32f2f" }}>
                        {formik?.errors?.dob}
                      </FormHelperText>
                    )}
                    <Typography variant="caption" color="rgba(0,0,0,.34)">
                      {t("pandob")}
                    </Typography>
                  </FormControl>
                </LocalizationProvider>
              </Grid>
            )}
          </Grid>

          <Stack
            alignItems="center"
            justifyContent={"center"}
            my={4}
            mx={"auto"}
          >
            <SecureDataComponent />
            <ButtonComponent
              toolTipTitle={t("tooltip")}
              type="submit"
              onClick={formik.handleSubmit}
              disabled={
                !(!customerData?.data?.firstName
                  ? formik.dirty && formik.isValid
                  : formik.isValid)
              }
              buttonStyle={{ width: "100%", mt: 2 }}
            >
              {t("continue")}
            </ButtonComponent>
          </Stack>
        </Stack>
      </PaperComponent>
    </Stack>
  );
}

export default CkycScreen;
